import { BO, BusinessObjectBase, ModelBase, NonUniqueModelDataError, PropertyChangedJoinPoint, QueryBus, RepositoryResponseConflictCodes, ResponseConflictObject, ValidationResult, Weaver } from '@skylab-shared/core';
import { ClientBankAccountModel, ClientKind, ClientModel, TinKind } from '@skylab-shared/dataModel';
import { sameValues, withFlag } from '@skylab-shared/utils';
import { ContextKeys } from '../common/context-keys';
import { EmailUtils } from '../common/email-utils';
import { propertyChangedFilter, propertyChangedJoinPointFilter, propertyChangedJoinPointFilter as propertyJoinPointFilter } from '../common/property-changed/property-changed-filter';
import { EmailValidator } from '../common/validators/email-validator';
import { NipValidator } from '../common/validators/nip-validator';
import { NrbValidator } from '../common/validators/nrb-validator';
import { PeselValidator } from '../common/validators/pesel-validator';
import { CountryCollectionQuery } from '../country/queries/country.query-base';
import { PaymentMethodQuery } from '../payment-method/queries/payment-method.query-base';
import { ClientsWithSameNameQueryProperties, ClientsWithSamePropertyQuery, ClientsWithSameTinQueryProperties } from './queries/clients-with-same-property.query-base';
import { BuyerCantBeTheSameAsClientError } from './violations/buyer-cant-be-the-same-as-client-error';
import { ClientInternalIdentificationIsInvalidError } from './violations/client-internal-identification-is-invalid-error';
import { ClientInternalIdentificationNipInvalidWarning } from './violations/client-internal-identification-nip-invalid-warning';
import { InvalidBankAccountNumberWarning } from './violations/invalid-bank-account-number-warning';
import { InvalidEmailWarning } from './violations/invalid-email-warning';
import { InvalidNipUeWarning } from './violations/invalid-nip-ue-warning';
import { InvalidNipWarning } from './violations/invalid-nip-warning';
import { InvalidPeselWarning } from './violations/invalid-pesel-warning';
import { NonUniqueBankAccountNumberWarning } from './violations/non-unique-bank-account-number';
import { NonUniqueNameWarning } from './violations/non-unique-name-warning';
import { NonUniqueTinWarning } from './violations/non-unique-tin-warning';
import { PersonNameShouldContainAtLeastOneSpace } from './violations/person-name-should-contain-at-least-one-space-warning';
import { TooLargeDefaultDiscountValueError } from './violations/too-large-default-discount-value-error';
import { TooSmallDefaultDiscountValueWarning } from './violations/too-small-default-discount-value-warning';

@BO({ modelType: ClientModel })
export class ClientBO extends BusinessObjectBase<ClientModel> {
    private static readonly clientKindToTinKind = {
        [ClientKind.Company]: TinKind.NIP,
        [ClientKind.Person]: TinKind.PESEL
    };

    private static readonly tinKindToClientKind = {
        [TinKind.NIP]: ClientKind.Company,
        [TinKind.VATIN]: ClientKind.Company,
        [TinKind.PESEL]: ClientKind.Person,
        [TinKind.OtherID]: ClientKind.Company
    };

    private currentDefaultBankAccount: ClientBankAccountModel | null = null;

    public constructor(
        weaver: Weaver,
        private readonly queryBus: QueryBus
    ) {
        super(weaver);
    }

    protected override handleConflictSaveResponse(conflict: ResponseConflictObject<RepositoryResponseConflictCodes>) {
        if ((conflict.code === RepositoryResponseConflictCodes.UniqueConstraintViolation) && sameValues(conflict.objectProperties!, ['name'])) {
            this.addExternalRuleViolations({
                [this.model.sun]: [{
                    property: 'name',
                    violation: new NonUniqueModelDataError('business.product.validation.server-rule-violations.non-unique-model-data-error')
                }]
            });
        }
    }

    public override async attachModelAsync(model: ClientModel): Promise<void> {
        await super.attachModelAsync(model);
        this.validateEmails();
    }

    protected override async initializeAsync(): Promise<void> {
        await super.initializeAsync();

        await Promise.all([
            this.localContext.preload(CountryCollectionQuery),
            this.localContext.preload(PaymentMethodQuery)
        ]);

        this.registerPropertyChangedListeners();
        this.registerValidationRules();
        this.configureReadonlyRules();
    }

    private registerPropertyChangedListeners(): void {
        this.registerDefaultDueDateSetting();
        this.registerOtherBuyerRelations();
        this.registerUpdateTinKindOnTinChange();
        this.adjustClientKindAndTaxIdNumberKind();
        this.registerBankAccountDefaultSynchronizer();
    }

    private registerValidationRules(): void {
        this.registerNameValidationRules();
        this.registerInternalIdentificationRules();
        this.registerBuyerRules();
        this.registerTaxIdentificationNumberRules();
        this.registerEmailRules();
        this.registerBankAccountNumbeRules();
        this.registerUniquenessRules();
        this.registerDefaultDiscountRules();
    }

    private registerBankAccountDefaultSynchronizer(): void {
        if (this.model.isPersistent) this.currentDefaultBankAccount = this.model.bankAccounts?.find(elem => elem.default) ?? null;

        this.registerListening(this.weaver.register(PropertyChangedJoinPoint, propertyChangedJoinPointFilter(this)(ClientBankAccountModel, ['default'])(
            ctx => {
                const model = ctx.model as ClientBankAccountModel;
                if (!model.default) return;

                this.currentDefaultBankAccount = model;
                this.model.bankAccounts.forEach(bankAcccount => {
                    if (bankAcccount === this.currentDefaultBankAccount) return;
                    bankAcccount.default = false;
                });
            }
        )));

        this.registerListening(this.model.bankAccounts.onCollectionChanged(_ => {
            if (!this.model.bankAccounts.length) return;

            const isDefault = this.model.bankAccounts.find(elem => elem.default);
            if (!isDefault) {
                this.model.bankAccounts[0].default = true;
                this.currentDefaultBankAccount = this.model.bankAccounts[0];
            }
        }));
    }

    private registerUpdateTinKindOnTinChange(): void {
        this.propertyChanged.listen<ClientModel>(propertyChangedFilter(ClientModel, ['tin'], ({ model }) => {
            if (!model.tin) return;

            const tinIsANumber = model.tin.replace(/[\s\-]+/g, '').match(/^\d+$/);
            if (model.tinKind === TinKind.NIP && !tinIsANumber) {
                model.tinKind = TinKind.VATIN;
            }
            if (model.tinKind === TinKind.VATIN && tinIsANumber) {
                model.tinKind = TinKind.NIP;
            }
        }));
    }

    private adjustClientKindAndTaxIdNumberKind(): void {
        this.propertyChanged.listen<ClientModel>(propertyChangedFilter(ClientModel, ['kind', 'tinKind'], ({ model, propertyName }) => {
            withFlag(() => {
                if (propertyName === 'kind') {
                    model.tinKind = ClientBO.clientKindToTinKind[model.kind];
                }
                if (propertyName === 'tinKind') {
                    model.kind = ClientBO.tinKindToClientKind[model.tinKind];
                }
            }, `${this.model.sun}#price-change`);
        }));
    }

    private registerOtherBuyerRelations(): void {
        this.propertyChanged.listen<ClientModel>(propertyChangedFilter(ClientModel, ['buyerId'], ({ model }) => {
            if (!model.buyerId) {
                model.internalIdentificationNumber = null;
                model.buyerRole = null;
            }
        }));
    }

    private registerDefaultDueDateSetting(): void {
        this.propertyChanged.listen<ClientModel>(propertyChangedFilter(ClientModel, ['defaultPaymentMethodId'], ({ model }) => {
            const paymentMethod = this.localContext.getPreloaded(PaymentMethodQuery)?.find(elem => elem.id === model.defaultPaymentMethodId);
            model.defaultDueDate = paymentMethod ? paymentMethod.dueDate : null;
        }));
    }

    private registerNameValidationRules(): void {
        this.validation.addRule(
            [[ClientModel, 'name'], [ClientModel, 'kind'], [ClientModel, 'tinKind']],
            [[ClientModel, 'name']],
            ctx => {
                if (!ctx.source.name?.trim() || (ctx.source.kind === ClientKind.Company)) return true;
                return ctx.source.name.trim().includes(' ');
            },
            PersonNameShouldContainAtLeastOneSpace
        );
    }

    private registerInternalIdentificationRules(): void {
        const ClientInternalIdentificationRegExp = /^[1-9]((\d[1-9])|([1-9]\d))\d{7}-\d{5}$/;
        const internalIdFilled = () => !!this.model.internalIdentificationNumber?.trim();
        const internalIdCorrectFormat = () => ClientInternalIdentificationRegExp.test(this.model.internalIdentificationNumber!);

        this.validation.addRule(
            [[ClientModel, 'internalIdentificationNumber'], [ClientModel, 'buyerId']],
            [[ClientModel, 'internalIdentificationNumber']],
            _ => {
                if (!this.model.buyerId || !internalIdFilled()) return true;
                return internalIdCorrectFormat();
            },
            ClientInternalIdentificationIsInvalidError
        );

        this.validation.addRule(
            [[ClientModel, 'internalIdentificationNumber'], [ClientModel, 'buyerId']],
            [[ClientModel, 'internalIdentificationNumber']],
            _ => {
                if (!this.model.buyerId || !internalIdFilled() || !internalIdCorrectFormat()) return true;
                const nip = this.model.internalIdentificationNumber?.split('-')[0];
                return NipValidator.isValid(nip);
            },
            ClientInternalIdentificationNipInvalidWarning
        );

    }

    private registerBuyerRules(): void {
        this.validation.addRule(
            [[ClientModel, 'buyerId']],
            ctx => {
                return ctx.source.buyerId !== ctx.source.id;
            },
            BuyerCantBeTheSameAsClientError
        );
    }

    private registerDefaultDiscountRules(): void {
        this.validation.addRule(
            [[ClientModel, 'defaultDiscount']],
            ctx => {
                return (ctx.source.defaultDiscount || 0) <= 1;
            },
            TooLargeDefaultDiscountValueError
        );

        this.validation.addRule(
            [[ClientModel, 'defaultDiscount']],
            ctx => {
                return (ctx.source.defaultDiscount || 0) >= 0;
            },
            TooSmallDefaultDiscountValueWarning
        );
    }

    private registerUniquenessRules(): void {
        this.validation.addRule(
            [[ClientModel, 'tin'], [ClientModel, 'tinKind']],
            [[ClientModel, 'tin']],
            async ctx => {
                if (!ctx.source.tin) return true;
                const uniqueTinQueryParam: ClientsWithSameTinQueryProperties = {
                    tin: ctx.source.tin,
                    tinKind: ctx.source.tinKind,
                    omitedClientId: ctx.source.id
                };

                try {
                    const query = new ClientsWithSamePropertyQuery(uniqueTinQueryParam);
                    return (await this.localContext.getAsync(ContextKeys.tin(ctx.source.tin, ctx.source.tinKind), () => this.queryBus.queryAsync(query))).length === 0;
                } catch (err) {
                    return true;
                }
            },
            NonUniqueTinWarning
        );

        this.validation.addRule(
            [[ClientModel, 'name']],
            async ctx => {
                if (!ctx.source.name) return true;
                const uniqueNameQueryParam: ClientsWithSameNameQueryProperties = {
                    exactName: ctx.source.name,
                    omitedClientId: ctx.source.id
                };

                try {
                    const query = new ClientsWithSamePropertyQuery(uniqueNameQueryParam);
                    return (await this.localContext.getAsync(ContextKeys.clientName(ctx.source.name), () => this.queryBus.queryAsync(query))).length === 0;
                } catch (err) {
                    return true;
                }
            },
            NonUniqueNameWarning
        );
    }

    private registerTaxIdentificationNumberRules(): void {
        this.validation.addRule(
            [[ClientModel, 'tin'], [ClientModel, 'tinKind']],
            [[ClientModel, 'tin']],
            ctx => {
                if (ctx.source.tinKind === TinKind.NIP && ctx.source.tin)
                    return NipValidator.isValid(ctx.source.tin);
                return true;
            },
            InvalidNipWarning
        );

        this.validation.addRule(
            [[ClientModel, 'tin'], [ClientModel, 'tinKind']],
            [[ClientModel, 'tin']],
            ctx => {
                if (ctx.source.tinKind === TinKind.VATIN && ctx.source.tin) {
                    const country = ctx.bo.localContext.firstPreloaded(CountryCollectionQuery, {
                        euMember: true,
                        code: ctx.source.tin.substring(0, 2)
                    });
                    return !!country;
                }
                return true;
            },
            InvalidNipUeWarning
        );

        this.validation.addRule(
            [[ClientModel, 'tin'], [ClientModel, 'tinKind']],
            [[ClientModel, 'tin']],
            ctx => {
                if (ctx.source.tinKind == TinKind.PESEL && ctx.source.tin)
                    return PeselValidator.isValid(ctx.source.tin);
                return true;
            },
            InvalidPeselWarning
        );
    }

    private validateEmails(): void {
        this.validation.removeExternalValidationResults('email-validation');
        if (!EmailValidator.isValidEmails(EmailUtils.concatEmails(this.model.emails))) {
            const invalidEmails = EmailUtils.concatEmails(EmailValidator.getInvalidEmailsFromArray(this.model.emails));
            if (!invalidEmails) return;

            this.validation.addExternalValidationResult(<ValidationResult<ModelBase>>{
                source: this.model,
                propertyName: <keyof ClientModel>'emails',
                ruleViolation: {
                    rule: null,
                    context: null,
                    violation: new InvalidEmailWarning({ emails: invalidEmails })
                }
            }, 'email-validation');
        }
    }

    private registerEmailRules(): void {
        this.registerListening(this.weaver.register(PropertyChangedJoinPoint, propertyJoinPointFilter(this)(ClientModel, ['emails'])(
            ctx => this.validateEmails()
        )));
    }

    private registerBankAccountNumbeRules(): void {
        this.validation.addRule(
            [[ClientBankAccountModel, 'number']],
            ctx => NrbValidator.isValid(ctx.source.number),
            InvalidBankAccountNumberWarning
        );

        this.validation.addRule(
            [[ClientBankAccountModel, 'number']],
            ctx => ctx.bo.model.bankAccounts
                .filter(ba => normalizeBankAccount(ba.number) === normalizeBankAccount(ctx.source.number)).length <= 1,
            NonUniqueBankAccountNumberWarning
        );

        const normalizeBankAccount = (number: string) => number.replace(/[^a-zA-Z0-9]/g, '');
    }

    private configureReadonlyRules(): void {
        this.readonly.addRule(
            [[ClientModel, 'defaultPaymentMethodId']],
            [[ClientModel, 'defaultDueDate']],
            _ => this.model.defaultPaymentMethodId === null
        );

        this.readonly.addRule(
            [[ClientBankAccountModel, 'default']],
            ctx => ctx.source === this.currentDefaultBankAccount
        );

        this.readonly.addRule(
            [[ClientModel, 'buyerId']],
            [[ClientModel, 'buyerRole'], [ClientModel, 'internalIdentificationNumber']],
            ctx => !this.model.buyerId
        );
    }
}
